import { initConfigFromUrl, parseConfigFromUrl } from './authFlowFlags';
import { getMainInputUserData } from './handleMainInputData';

export type StorageKey =
  | 'survey'
  | 'toolkitSelection'
  | 'trial'
  | 'getSubscription'
  | 'invite'
  | 'contactInfo'
  | 'marketingSource'
  | 'redirectTo'
  | 'custom'
  | 'src'
  | 'isNew'
  | 'offer'
  | 'tk'
  | 'promo'
  | 'promocode'
  | 'currency'
  | 'chosenTrial'
  | 'userId'
  | 'paymentStatus'
  | 'userData'
  | 'signupFlowEvents'
  | 'copilotData'
  | 'hadTrial'
  | 'offerInfo'
  | 'toolkit'
  | 'surveyAnswers'
  | 'experiments'
  | 'personalisedPlansEligible'
  | 'entSolFlowEligible'
  | 'flowTheme'
  | 'previousPage'
  | 'wowAISeoFirstScreenShown'
  | 'aiSeoData'
  | 'bookEnterpriseSuccess';

const generateTraceNumber = (): number => {
  return Math.floor(new Date().valueOf() * Math.random());
};

class SessionStorageManager {
  field: 'WT_AUTH_FLOW';
  nowTimestamp: () => number;
  pack: (value, ttl?: number) => string;
  extract: (itemStr: string) => any;

  constructor() {
    this.field = 'WT_AUTH_FLOW';

    this.nowTimestamp = (): number => {
      return Math.round(Date.now() / 1000);
    };

    this.pack = (value, ttl: number = 86400): string => {
      let expired;

      try {
        const authFlowData = sessionStorage.getItem(this.field) || '';
        const storage = JSON.parse(authFlowData);
        expired = storage.expired ? storage.expired : this.nowTimestamp() + ttl;
      } catch (e) {
        expired = this.nowTimestamp() + ttl;
      }

      return JSON.stringify({ ...value, expired });
    };

    this.extract = (key: StorageKey) => {
      try {
        const authFlowData = sessionStorage.getItem(this.field) || '';
        const storage = JSON.parse(authFlowData);
        const item = storage[key];
        const expired = storage.expired;
        return this.nowTimestamp() < expired ? item : '';
      } catch (e) {
        return null;
      }
    };
  }

  init = (reset: boolean = false): void => {
    let config;

    if (reset) {
      this.removeAll();
      config = initConfigFromUrl();
    } else {
      config = parseConfigFromUrl();
    }

    this.set(config);
    const signupFlowEvents = this.get('signupFlowEvents') || {
      eventActions: [],
      eventLabels: [],
      traceId: generateTraceNumber(),
    };
    signupFlowEvents.traceId =
      signupFlowEvents?.traceId || generateTraceNumber();
    this.set({ signupFlowEvents });

    const currentUserData = this.get('userData');
    const mainInputUserData = getMainInputUserData();

    this.set({
      userData: {
        ...currentUserData,
        ...mainInputUserData,
      },
    });
  };

  getAll = () => {
    try {
      const authFlowData = sessionStorage.getItem(this.field) || '';
      return JSON.parse(authFlowData);
    } catch (e) {
      return {};
    }
  };

  get = (key: StorageKey) => {
    return this.extract(key);
  };

  set = (value, ttl: number = 3600): void => {
    const prevStorage = this.getAll();
    const val = {
      ...prevStorage,
      ...value,
    };

    try {
      sessionStorage.setItem(this.field, this.pack(val, ttl));
    } catch (e) {
      console.warn('Error: sessionStorage is disabled', e);
    }
  };

  remove = (key: StorageKey): void => {
    const storage = this.getAll();
    if (storage) {
      delete storage[key];

      try {
        sessionStorage.setItem(this.field, JSON.stringify(storage));
      } catch (e) {
        console.warn('Error: sessionStorage is disabled', e);
      }
    }
  };

  removeAll = () => {
    try {
      sessionStorage.removeItem(this.field);
    } catch (e) {
      console.warn('Error: sessionStorage is disabled', e);
    }
  };
}

export const storageManager = new SessionStorageManager();
